import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import {
  addVoucherToCart,
  fetchCartVoucherItems,
} from '../../../actions/action_cart_voucher_items';
import { useCurrentVoucherOrderKey } from '../../../hooks/useCurrentVoucherOrderKey';

const PageWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 24px 16px;
`;

const BackButton = styled.button`
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  margin-bottom: 16px;
  padding: 10px 16px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;

  &:hover {
    color: #374151;
    background-color: #f9fafb;
  }
`;

const ProductCard = styled.div`
  background-color: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const ProductImage = styled.div`
  width: 100%;
  height: 200px;
  padding: 16px;
  background-color: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const ProductContent = styled.div`
  padding: 24px;
`;

const ProductName = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
`;

const ProductDescription = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 24px 0;
`;

const PriceSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background-color: #f9fafb;
  border-radius: 8px;
  margin-bottom: 24px;
`;

const PriceLabel = styled.div`
  font-size: 16px;
  color: #666;
`;

const PriceValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #333;
`;

const ValueSection = styled.div`
  background-color: #ecfdf5;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
`;

const ValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
    padding-top: 12px;
    border-top: 1px dashed #86efac;
    font-weight: 600;
  }
`;

const ValueLabel = styled.span`
  font-size: 15px;
  color: #166534;
`;

const ValueAmount = styled.span`
  font-size: 15px;
  color: #166534;
  font-weight: 600;
`;

const BonusBadge = styled.span`
  background-color: #fbbf24;
  color: #78350f;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
`;

const ExpirationNotice = styled.div`
  font-size: 13px;
  color: #92400e;
  background-color: #fef3c7;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 24px;
`;

const TermsSection = styled.div`
  margin-bottom: 24px;
`;

const TermsCheckbox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;

  input {
    margin-top: 4px;
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  span {
    font-size: 14px;
    color: #333;
    line-height: 1.5;
  }

  a {
    color: #22c55e;
    text-decoration: underline;

    &:hover {
      color: #16a34a;
    }
  }
`;

const QuantitySection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const QuantityLabel = styled.span`
  font-size: 15px;
  color: #333;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const QuantityButton = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid #d1d5db;
  background-color: white;
  border-radius: 6px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: #f3f4f6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityValue = styled.span`
  font-size: 18px;
  font-weight: 600;
  min-width: 40px;
  text-align: center;
`;

const AddToCartButton = styled.button`
  width: 100%;
  padding: 16px 24px;
  background-color: ${(props) => (props.disabled ? '#9ca3af' : '#22c55e')};
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #16a34a;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  border: 1px solid #ef4444;
  border-radius: 8px;
  padding: 12px 16px;
  color: #dc2626;
  margin-bottom: 16px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

const NotFoundMessage = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #666;
`;

function VoucherProductDetail({ match, history, location }) {
  const dispatch = useDispatch();
  const productId = match.params.id;
  const currentOrderKey = useCurrentVoucherOrderKey();
  // Prefer query param orderKey if provided, otherwise use current order context from Redux
  const orderKey =
    new URLSearchParams(location.search).get('orderKey') || currentOrderKey;

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get voucher products from shoot state (where they're serialized alongside CPM products)
  const shoot = useSelector((state) => state.shoot);
  const voucherTermsUrl = useSelector((state) => state.shoot.voucherTermsUrl);

  // Find the voucher product from the shoot data
  // Vouchers can be in two places:
  // 1. Standalone vouchers in shoot.voucherProducts
  // 2. In-group vouchers inside shoot.shootProducts[].products[]
  const voucherProducts = shoot?.voucherProducts || [];
  const shootProducts = shoot?.shootProducts || [];

  // Search for voucher in shootProducts (product groups)
  const findVoucherInShootProducts = () => {
    for (const category of shootProducts) {
      const found = category.products?.find(
        (p) =>
          p.is_voucher &&
          (String(p.voucher_product_id) === String(productId) ||
            String(p.id) === String(productId)),
      );
      if (found) return found;
    }
    return null;
  };

  // Search standalone voucherProducts first, then shootProducts
  const product =
    voucherProducts.find(
      (p) =>
        String(p.voucher_product_id) === String(productId) ||
        String(p.id) === String(productId),
    ) || findVoucherInShootProducts();

  const formatCurrency = (cents) => `$${(cents / 100).toFixed(2)}`;

  const handleBack = () => {
    history.goBack();
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
  };

  const handleQuantityChange = (delta) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = async () => {
    if (!product || !termsAccepted) return;

    setLoading(true);
    setError(null);

    try {
      const result = await dispatch(
        addVoucherToCart(
          product.voucher_product_id || product.id,
          quantity,
          termsAccepted,
          orderKey,
        ),
      );

      if (result.error) {
        setError(result.error);
      } else {
        toast.success('Voucher added to cart!');
        // Refresh cart voucher items for full state sync (includes terms_accepted_at)
        dispatch(fetchCartVoucherItems(result.cartUuid));
        // Middleware will handle routing based on incentive eligibility
        // If middleware doesn't route (no incentives), navigate to offers
        // Note: Middleware routes happen synchronously in the dispatch above
      }
    } catch (err) {
      setError('Failed to add voucher to cart. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!shoot || !shoot.shoot || Object.keys(shoot.shoot).length === 0) {
    return (
      <PageWrapper>
        <LoadingWrapper>
          <div className="spinner" />
        </LoadingWrapper>
      </PageWrapper>
    );
  }

  if (!product) {
    return (
      <PageWrapper>
        <BackButton onClick={handleBack}>&larr; Back</BackButton>
        <NotFoundMessage>
          <h2>Voucher Product Not Found</h2>
          <p>The voucher product you're looking for is not available.</p>
        </NotFoundMessage>
      </PageWrapper>
    );
  }

  const hasPromo = product.promo_value_cents > 0;
  const savings = product.total_value_cents - product.purchase_price_cents;
  const hasSavings = savings > 0;

  return (
    <PageWrapper>
      <BackButton onClick={handleBack}>&larr; Back</BackButton>

      <ProductCard>
        {product.image_thumbnail && (
          <ProductImage>
            <img
              src={product.image_large || product.image_thumbnail}
              alt={product.name}
            />
          </ProductImage>
        )}

        <ProductContent>
          <ProductName>{product.name}</ProductName>
          <ProductDescription>
            {product.description ||
              'Purchase credit to use on your photo orders.'}
          </ProductDescription>

          <PriceSection>
            <PriceLabel>You Pay</PriceLabel>
            <PriceValue>{formatCurrency(product.price)}</PriceValue>
          </PriceSection>

          <ValueSection>
            <ValueRow>
              <ValueLabel>Purchased Credit</ValueLabel>
              <ValueAmount>
                {formatCurrency(product.purchased_value_cents)}
              </ValueAmount>
            </ValueRow>
            {hasPromo && (
              <ValueRow>
                <ValueLabel>
                  Bonus Credit
                  <BonusBadge>FREE</BonusBadge>
                </ValueLabel>
                <ValueAmount>
                  {formatCurrency(product.promo_value_cents)}
                </ValueAmount>
              </ValueRow>
            )}
            <ValueRow>
              <ValueLabel>Total to Spend</ValueLabel>
              <ValueAmount style={{ fontSize: '18px' }}>
                {formatCurrency(product.total_value_cents)}
              </ValueAmount>
            </ValueRow>
            {hasSavings && (
              <ValueRow style={{ borderTop: 'none', paddingTop: 0 }}>
                <ValueLabel style={{ color: '#dc2626' }}>You Save</ValueLabel>
                <ValueAmount style={{ color: '#dc2626' }}>
                  {formatCurrency(savings)}
                </ValueAmount>
              </ValueRow>
            )}
          </ValueSection>

          {hasPromo && product.promo_expires && (
            <ExpirationNotice>
              <strong>Note:</strong> Bonus credit expires{' '}
              {product.promo_expiration_days} days after purchase. Purchased
              credit never expires.
            </ExpirationNotice>
          )}

          <TermsSection>
            <TermsCheckbox>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={handleTermsChange}
              />
              <span>
                I understand this Voucher Credit is intended for Spring Picture
                orders
                {hasPromo && product.promo_expires
                  ? `, and bonus credit expires ${product.promo_expiration_days} days after purchase`
                  : ''}
                .
                {voucherTermsUrl && /^https?:\/\//.test(voucherTermsUrl) && (
                  <>
                    {' '}
                    I agree to the{' '}
                    <a href={voucherTermsUrl} target="_blank" rel="noreferrer">
                      terms and conditions
                    </a>
                    .
                  </>
                )}
              </span>
            </TermsCheckbox>
          </TermsSection>

          <QuantitySection>
            <QuantityLabel>Quantity:</QuantityLabel>
            <QuantityControls>
              <QuantityButton
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
              >
                -
              </QuantityButton>
              <QuantityValue>{quantity}</QuantityValue>
              <QuantityButton
                onClick={() => handleQuantityChange(1)}
                disabled={quantity >= 10}
              >
                +
              </QuantityButton>
            </QuantityControls>
          </QuantitySection>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <AddToCartButton
            disabled={!termsAccepted || loading}
            onClick={handleAddToCart}
          >
            {loading
              ? 'Adding to Cart...'
              : `Add to Cart - ${formatCurrency(product.price * quantity)}`}
          </AddToCartButton>
        </ProductContent>
      </ProductCard>
    </PageWrapper>
  );
}

export default withRouter(VoucherProductDetail);
